/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Injector} from '../di/injector';
import {Type} from '../type';
import {stringify} from '../util';

import {ComponentFactory, ComponentRef} from './component_factory';
import {NgModuleRef} from './ng_module_factory';
/**
 * @param {?} component
 * @return {?}
 */
export function noComponentFactoryError(component: Function) {
  const /** @type {?} */ error = Error(
      `No component factory found for ${stringify(component)}. Did you add it to @NgModule.entryComponents?`);
  ( /** @type {?} */((error as any)))[ERROR_COMPONENT] = component;
  return error;
}

const /** @type {?} */ ERROR_COMPONENT = 'ngComponent';
/**
 * @param {?} error
 * @return {?}
 */
export function getComponent(error: Error): Type<any> {
  return ( /** @type {?} */((error as any)))[ERROR_COMPONENT];
}
class _NullComponentFactoryResolver implements ComponentFactoryResolver {
/**
 * @template T
 * @param {?} component
 * @return {?}
 */
resolveComponentFactory<T>(component: {new (...args: any[]): T}): ComponentFactory<T> {
    throw noComponentFactoryError(component);
  }
}
/**
 * \@stable
 * @abstract
 */
export abstract class ComponentFactoryResolver {
  static NULL: ComponentFactoryResolver = new _NullComponentFactoryResolver();
/**
 * @abstract
 * @template T
 * @param {?} component
 * @return {?}
 */
resolveComponentFactory<T>(component: Type<T>) {}
}

function ComponentFactoryResolver_tsickle_Closure_declarations() {
/** @type {?} */
ComponentFactoryResolver.NULL;
}

export class CodegenComponentFactoryResolver implements ComponentFactoryResolver {
private _factories = new Map<any, ComponentFactory<any>>();
/**
 * @param {?} factories
 * @param {?} _parent
 * @param {?} _ngModule
 */
constructor(
      factories: ComponentFactory<any>[],
private _parent: ComponentFactoryResolver,
private _ngModule: NgModuleRef<any>) {
    for (let i = 0; i < factories.length; i++) {
      const factory = factories[i];
      this._factories.set(factory.componentType, factory);
    }
  }
/**
 * @template T
 * @param {?} component
 * @return {?}
 */
resolveComponentFactory<T>(component: {new (...args: any[]): T}): ComponentFactory<T> {
    let /** @type {?} */ factory = this._factories.get(component);
    if (!factory && this._parent) {
      factory = this._parent.resolveComponentFactory(component);
    }
    if (!factory) {
      throw noComponentFactoryError(component);
    }
    return new ComponentFactoryBoundToModule(factory, this._ngModule);
  }
}

function CodegenComponentFactoryResolver_tsickle_Closure_declarations() {
/** @type {?} */
CodegenComponentFactoryResolver.prototype._factories;
/** @type {?} */
CodegenComponentFactoryResolver.prototype._parent;
/** @type {?} */
CodegenComponentFactoryResolver.prototype._ngModule;
}

export class ComponentFactoryBoundToModule<C> extends ComponentFactory<C> {
/**
 * @param {?} factory
 * @param {?} ngModule
 */
constructor(private factory: ComponentFactory<C>,
private ngModule: NgModuleRef<any>) { super(); }
/**
 * @return {?}
 */
get selector() { return this.factory.selector; }
/**
 * @return {?}
 */
get componentType() { return this.factory.componentType; }
/**
 * @return {?}
 */
get ngContentSelectors() { return this.factory.ngContentSelectors; }
/**
 * @return {?}
 */
get inputs() { return this.factory.inputs; }
/**
 * @return {?}
 */
get outputs() { return this.factory.outputs; }
/**
 * @param {?} injector
 * @param {?=} projectableNodes
 * @param {?=} rootSelectorOrNode
 * @param {?=} ngModule
 * @return {?}
 */
create(
      injector: Injector, projectableNodes?: any[][], rootSelectorOrNode?: string|any,
      ngModule?: NgModuleRef<any>): ComponentRef<C> {
    return this.factory.create(
        injector, projectableNodes, rootSelectorOrNode, ngModule || this.ngModule);
  }
}

function ComponentFactoryBoundToModule_tsickle_Closure_declarations() {
/** @type {?} */
ComponentFactoryBoundToModule.prototype.factory;
/** @type {?} */
ComponentFactoryBoundToModule.prototype.ngModule;
}

